import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV_LINKS, BUSINESS, LOGO_URL } from "@/lib/constants";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="site-logo">
      <img
        src={LOGO_URL}
        alt="FASTag CSP Sayeed Anwar Bally logo"
        className="w-11 h-11 rounded-full object-contain bg-white ring-1 ring-[#E2E8F0] group-hover:scale-105 transition-transform duration-200"
        loading="eager"
        decoding="async"
      />
      <div className="leading-tight">
        <div className="font-display font-bold text-[#111827] text-base sm:text-lg">FASTag CSP</div>
        <div className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-[#0E7490]">Sayeed Anwar · Bally</div>
      </div>
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
      isActive ? "text-[#0057B8] bg-[#0057B8]/10" : "text-[#111827] hover:text-[#0057B8] hover:bg-[#0057B8]/5"
    }`;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button asChild variant="outline" className="border-[#0057B8] text-[#0057B8] hover:bg-[#0057B8] hover:text-white rounded-full btn-brand">
            <a href={`tel:${BUSINESS.phone}`} data-testid="header-call-btn">
              <Phone className="w-4 h-4 mr-1.5" /> Call Now
            </a>
          </Button>
          <Button asChild className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full btn-brand shadow-sm active:scale-95">
            <Link to="/order" data-testid="header-order-btn">Order FASTag</Link>
          </Button>
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <div className="flex items-center justify-between mb-6">
                <Logo />
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close menu">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-lg text-base font-medium ${
                        isActive ? "bg-[#0057B8] text-white" : "text-[#111827] hover:bg-[#0057B8]/5"
                      }`
                    }
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <Button asChild className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full btn-brand">
                    <Link to="/order" onClick={() => setOpen(false)}>Order FASTag</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-[#0057B8] text-[#0057B8] rounded-full btn-brand">
                    <a href={`tel:${BUSINESS.phone}`}><Phone className="w-4 h-4 mr-1.5" /> {BUSINESS.phoneDisplay}</a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
